import React, { useCallback, useEffect, useState } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { ShieldAlert, ArrowLeft, Flag, Loader2, Trash2, CheckCircle2, XCircle, EyeOff, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import BottomNav from '../components/BottomNav';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const REASON_LABEL = {
  'harassment':     'Harassment',
  'spam':           'Spam',
  'hate-speech':    'Hate speech',
  'self-harm':      'Self-harm concern',
  'misinformation': 'Misinformation',
  'off-topic':      'Off-topic',
  'other':          'Other',
};

const timeAgo = (iso) => {
  if (!iso) return '';
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

const ModerationPage = () => {
  const { user, isLoading: loading } = useAuth();
  const [tab,   setTab]   = useState('pending');   // pending | resolved | dismissed | all | hidden
  const [items, setItems] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [fetching, setFetching] = useState(true);

  const authHeaders = useCallback(() => {
    const t = localStorage.getItem('rbr_token');
    return t ? { Authorization: `Bearer ${t}` } : {};
  }, []);

  const load = useCallback(async () => {
    setFetching(true);
    try {
      // Prompt #7 Task 4 — the 'hidden' tab uses a different endpoint that
      // returns auto-hidden COMMENTS (with their reports attached) rather
      // than raw report rows.
      const url = tab === 'hidden'
        ? `${API_URL}/api/admin/comments/hidden`
        : `${API_URL}/api/admin/reports?status=${tab}`;
      const res = await fetch(url, { headers: { ...authHeaders() } });
      if (res.status === 403) { setItems([]); return; }
      const body = await res.json();
      setItems(tab === 'hidden' ? (body.items || []) : (body.reports || []));
    } catch {
      toast.error('Could not load reports');
    } finally {
      setFetching(false);
    }
  }, [tab, authHeaders]);

  useEffect(() => { if (user?.isAdmin) load(); }, [user?.isAdmin, load]);

  // Gate the page: only admins can even see it.
  if (loading) return null;
  if (!user)          return <Navigate to="/auth" replace />;
  if (!user.isAdmin)  return <Navigate to="/" replace />;

  const actOn = async (id, kind) => {
    if (kind === 'resolve' && !window.confirm('Delete this comment and close the report?')) return;
    setBusyId(id);
    try {
      const res = await fetch(`${API_URL}/api/admin/reports/${id}/${kind}`, {
        method: 'POST',
        headers: { ...authHeaders() },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        toast.error(body.detail || 'Action failed');
      } else {
        toast.success(kind === 'resolve' ? 'Comment removed 🛡️' : 'Report dismissed');
        setItems((prev) => prev.filter((r) => r.id !== id));
      }
    } catch {
      toast.error('Network error');
    } finally {
      setBusyId(null);
    }
  };

  const restoreComment = async (commentId) => {
    if (!window.confirm('Restore this auto-hidden comment? Pending reports will be dismissed.')) return;
    setBusyId(commentId);
    try {
      const res = await fetch(`${API_URL}/api/admin/comments/${commentId}/restore`, {
        method: 'POST',
        headers: { ...authHeaders() },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        toast.error(body.detail || 'Restore failed');
      } else {
        toast.success('Comment restored 🌿');
        setItems((prev) => prev.filter((c) => c.id !== commentId));
      }
    } catch {
      toast.error('Network error');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="min-h-screen pb-24" style={{ background: 'linear-gradient(180deg, #1c3a33 0%, #0f1f1c 100%)' }} data-testid="moderation-page">
      {/* Header */}
      <div className="px-5 pt-4 pb-2">
        <Link
          to="/"
          data-testid="moderation-back-btn"
          className="inline-flex items-center gap-1.5 text-[11px] text-white/60 hover:text-white transition-colors mb-3"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back home
        </Link>
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg"
               style={{ background: 'linear-gradient(135deg, #E76F51 0%, #B45539 100%)' }}>
            <ShieldAlert className="w-6 h-6 text-white" />
          </div>
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-orange-400">
              Moderation
            </p>
            <h1 className="text-2xl text-[#F8F6F0]" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 600 }}>
              Report Queue
            </h1>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="px-5 mt-4 flex gap-1.5 overflow-x-auto no-scrollbar pb-2" data-testid="moderation-tabs">
        {['pending', 'hidden', 'resolved', 'dismissed', 'all'].map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className={`text-[11px] font-semibold uppercase tracking-wide px-3 py-1.5 rounded-full flex-shrink-0 transition-all active:scale-95 ${
              tab === t
                ? 'bg-[#D6AD60] text-[#242824]'
                : 'bg-white/10 text-white/80 border border-white/25'
            }`}
            data-testid={`moderation-tab-${t}`}
          >
            {t}
          </button>
        ))}
      </div>

      {/* List */}
      <div className="px-5 mt-4">
        {fetching ? (
          <div className="text-center py-10 text-white/60 text-sm flex items-center justify-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> loading…
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-14 rounded-2xl bg-white/[0.03] border border-white/10">
            <p className="text-[13px] text-white/70">Nothing to review here.</p>
            <p className="text-[11px] text-white/50 mt-1">
              {tab === 'pending' ? 'Inbox zero. Nicely done. 🌱' : 'This tab is empty.'}
            </p>
          </div>
        ) : tab === 'hidden' ? (
          <ul className="space-y-3" data-testid="moderation-hidden-list">
            {items.map((c) => (
              <li key={c.id} className="rounded-2xl p-4 bg-[#FFFCF3] border border-orange-200" data-testid={`moderation-hidden-${c.id}`}>
                <div className="flex items-center gap-2 flex-wrap mb-2">
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider text-orange-700 bg-orange-100 border border-orange-200">
                    <EyeOff className="w-3 h-3" /> Auto-hidden · {c.pendingReports || 0} pending
                  </span>
                  <span className="text-[10px] text-stone-400">
                    hidden {timeAgo(c.autoHiddenAt)} · by {c.userName || 'user'}
                  </span>
                </div>
                <div className="rounded-xl p-3 bg-stone-50 border border-stone-200 mb-3">
                  <p className="text-[13px] leading-relaxed text-stone-800 whitespace-pre-line break-words">
                    {c.text}
                  </p>
                </div>
                {c.reports && c.reports.length > 0 && (
                  <details className="mb-3">
                    <summary className="text-[11px] text-stone-500 cursor-pointer">
                      {c.reports.length} report{c.reports.length === 1 ? '' : 's'} · view evidence
                    </summary>
                    <ul className="mt-2 space-y-1.5">
                      {c.reports.slice(0, 8).map((r) => (
                        <li key={r.id} className="text-[11px] text-stone-600 pl-2 border-l border-stone-300">
                          <b>{REASON_LABEL[r.reason] || r.reason}</b>
                          {r.detail ? ` — "${r.detail}"` : ''}
                          <span className="text-stone-400"> · {timeAgo(r.createdAt)} · {r.status}</span>
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
                <div className="flex items-center justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => restoreComment(c.id)}
                    disabled={busyId === c.id}
                    data-testid={`moderation-restore-${c.id}`}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-semibold text-white bg-green-600 hover:bg-green-700 disabled:opacity-60"
                  >
                    {busyId === c.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
                    Restore
                  </button>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <ul className="space-y-3">
            {items.map((r) => {
              const c = r.comment;
              return (
                <li key={r.id} className="rounded-2xl p-4 bg-[#FFFCF3] border border-orange-200" data-testid={`moderation-report-${r.id}`}>
                  <div className="flex items-center gap-2 flex-wrap mb-2">
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider text-orange-700 bg-orange-100 border border-orange-200">
                      <Flag className="w-3 h-3" /> {REASON_LABEL[r.reason] || r.reason}
                    </span>
                    <span className="text-[10px] text-stone-400">
                      reported {timeAgo(r.createdAt)} by <b className="text-stone-600">{r.reporterName}</b>
                    </span>
                    {r.status !== 'pending' && (
                      <span className={`text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-full ${
                        r.status === 'resolved' ? 'bg-green-100 text-green-700' : 'bg-stone-100 text-stone-600'
                      }`}>
                        {r.status}
                      </span>
                    )}
                  </div>
                  {r.detail && (
                    <p className="text-[12px] text-stone-500 italic mb-2 whitespace-pre-line">
                      &ldquo;{r.detail}&rdquo;
                    </p>
                  )}
                  {c ? (
                    <div className="rounded-xl p-3 bg-stone-50 border border-stone-200">
                      <p className="text-[10px] uppercase tracking-wide text-stone-400 mb-1">
                        Reported comment · by {c.userName}
                      </p>
                      <p className="text-[13px] leading-relaxed text-stone-800 whitespace-pre-line break-words">
                        {c.content}
                      </p>
                    </div>
                  ) : (
                    <p className="text-[12px] text-stone-400 italic">Comment already deleted.</p>
                  )}
                  {r.status === 'pending' && (
                    <div className="flex items-center justify-end gap-2 mt-3">
                      <button
                        type="button"
                        onClick={() => actOn(r.id, 'dismiss')}
                        disabled={busyId === r.id}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-semibold text-stone-600 bg-stone-100 hover:bg-stone-200 border border-stone-200 disabled:opacity-60"
                        data-testid={`moderation-dismiss-${r.id}`}
                      >
                        <XCircle className="w-3.5 h-3.5" /> Dismiss
                      </button>
                      <button
                        type="button"
                        onClick={() => actOn(r.id, 'resolve')}
                        disabled={busyId === r.id || !c}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-semibold text-white bg-orange-500 hover:bg-orange-600 disabled:opacity-60"
                        data-testid={`moderation-resolve-${r.id}`}
                      >
                        {busyId === r.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                        Delete comment
                      </button>
                    </div>
                  )}
                  {r.status !== 'pending' && r.resolutionAction && (
                    <p className="text-[10px] text-stone-400 mt-2 inline-flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3" />
                      Resolution: {r.resolutionAction === 'deleted' ? 'comment deleted' : 'no action'}
                    </p>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <BottomNav />
    </div>
  );
};

export default ModerationPage;
